// shared-components/services/AdminProductService.ts

import { supabase } from "@shared/core/services/SupabaseService";
import { activityLogService } from "../monitoring/ActivityLogService.js";
import { MoneyService } from "../services/MoneyService.js";

import type {
  ProductWithDigital,
  DigitalProductFormData,
  ProductType,
  DigitalMetadata,
} from "../types/DigitalProductTypes";

// Initialize activity log service
activityLogService.initialize().catch(console.error);

interface ProductCategory {
  id: string;
  name: string;
}

interface ProductData {
  name: string;
  description?: string;
  price: number;
  image?: string | null;
  in_stock?: boolean;
  stock_quantity?: number;
  max_quantity?: number | null;
  category_id?: string | null;
  status?: "draft" | "published" | "archived";
  product_type?: ProductType;
  digital_metadata?: DigitalMetadata | null;
}

interface CanDeleteResult {
  canDelete: boolean;
  activeOrderCount: number;
  shouldArchive: boolean;
  totalOrderCount: number;
  downloadCount: number;
  recommendation: "archive" | "delete";
}

class AdminProductService {
  private tableName: string;
  private websiteId: string | null;

  constructor() {
    this.tableName = "products";
    this.websiteId = null;
  }

  setWebsiteId(websiteId: string): void {
    this.websiteId = websiteId;
  }

  getWebsiteId(): string {
    if (!this.websiteId) {
      this.websiteId = import.meta.env.VITE_WEBSITE_ID;
    }

    if (!this.websiteId) {
      throw new Error(
        "Website ID is not set. Please configure VITE_WEBSITE_ID in your environment variables.",
      );
    }

    return this.websiteId;
  }

  async getAllProducts(
    includeArchived: boolean = true,
  ): Promise<ProductWithDigital[]> {
    let query = supabase
      .from(this.tableName)
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .eq("website_id", this.getWebsiteId());

    // Exclude archived products unless explicitly requested
    if (!includeArchived) {
      query = query.neq("status", "archived");
    }

    const { data, error } = await query.order("created_at", {
      ascending: false,
    });

    if (error) throw error;
    return data || [];
  }

  // Method to get only archived products
  async getArchivedProducts(): Promise<ProductWithDigital[]> {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .eq("website_id", this.getWebsiteId())
      .eq("status", "archived")
      .order("updated_at", { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getPublishedProducts(): Promise<ProductWithDigital[]> {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .eq("website_id", this.getWebsiteId())
      .eq("status", "published")
      .eq("in_stock", true)
      .order("name", { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async getProductById(id: string): Promise<ProductWithDigital> {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .eq("id", id)
      .eq("website_id", this.getWebsiteId())
      .single();

    if (error) throw error;
    return data;
  }

  async createProduct(productData: ProductData): Promise<ProductWithDigital> {
    // Clean the data to ensure nulls are properly handled
    const cleanedData = {
      name: productData.name,
      description: productData.description,
      price: productData.price,
      image: productData.image || null,
      in_stock: productData.in_stock ?? true,
      stock_quantity: productData.stock_quantity || 0,
      max_quantity: productData.max_quantity || null,
      category_id: productData.category_id || null,
      status: productData.status || "draft",
      product_type: productData.product_type || "physical",
      digital_metadata: productData.digital_metadata || null,
      website_id: this.getWebsiteId(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from(this.tableName)
      .insert([cleanedData])
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .single();

    if (error) throw error;

    // Log the activity
    await activityLogService.logCreate(
      "product", // resource type
      data.id, // resource ID
      data.name, // resource name (for display)
      {
        // extra details
        price: data.price,
        status: data.status,
        in_stock: data.in_stock,
        product_type: data.product_type,
      },
    );

    return data;
  }

  async updateProduct(
    id: string,
    productData: ProductData,
  ): Promise<ProductWithDigital> {
    const oldProduct = await this.getProductById(id);

    // Clean the data to ensure nulls are properly handled
    const cleanedData = {
      name: productData.name,
      description: productData.description,
      price: productData.price,
      image: productData.image || null,
      in_stock: productData.in_stock ?? true,
      stock_quantity: productData.stock_quantity || 0,
      max_quantity: productData.max_quantity || null,
      category_id: productData.category_id || null,
      status: productData.status,
      product_type: productData.product_type || oldProduct.product_type,
      digital_metadata: productData.digital_metadata || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from(this.tableName)
      .update(cleanedData)
      .eq("id", id)
      .eq("website_id", this.getWebsiteId())
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .single();

    if (error) throw error;

    // Log the activity ONLY if there are actual changes
    const changes: Record<string, { from: unknown; to: unknown }> = {};
    if (oldProduct.name !== data.name)
      changes.name = { from: oldProduct.name, to: data.name };
    if (oldProduct.price !== data.price)
      changes.price = {
        from: MoneyService.format({
          amount: oldProduct.price,
          currency: "GBP",
        }),
        to: MoneyService.format({ amount: data.price, currency: "GBP" }),
      };
    if (oldProduct.status !== data.status)
      changes.status = { from: oldProduct.status, to: data.status };
    if (oldProduct.product_type !== data.product_type)
      changes.product_type = {
        from: oldProduct.product_type,
        to: data.product_type,
      };
    if (oldProduct.image !== data.image)
      changes.image = { from: oldProduct.image, to: data.image };

    // Only log if there are actual changes
    if (Object.keys(changes).length > 0) {
      await activityLogService.logUpdate("product", data.id, data.name, {
        changes,
      });
    }

    return data;
  }

  async getCategories(): Promise<ProductCategory[]> {
    const { data, error } = await supabase
      .from("product_categories")
      .select("*")
      .order("name");

    if (error) throw error;
    return data || [];
  }

  async deleteProduct(id: string): Promise<boolean> {
    // Initialize activity log service
    await activityLogService.initialize();

    // Get product info first
    let product: ProductWithDigital | null = null;
    let productName = "Unknown Product";

    try {
      product = await this.getProductById(id);
      productName = product.name;
    } catch (error) {
      console.warn("Could not fetch product details before deletion:", error);
    }

    try {
      // Check if product can be deleted or should be archived
      const deletionCheck = await this.canDeleteProduct(id);
      const {
        canDelete,
        shouldArchive,
        activeOrderCount,
        totalOrderCount,
        downloadCount,
      } = deletionCheck;

      // If product has any order history, archive instead of delete
      if (shouldArchive) {
        // Archive the product instead
        const _archivedProduct = await this.updateProductStatus(id, "archived");

        // Log the archival
        await activityLogService.logActivity(
          "archive",
          "product",
          id,
          productName,
          {
            reason: "order_history_preservation",
            total_orders: totalOrderCount,
            active_orders: activeOrderCount,
            downloads: downloadCount,
            previous_status: product?.status || "unknown",
          },
        );

        return true; // Return success - product was safely "deleted" via archival
      }

      if (!canDelete) {
        // Log the failed deletion attempt
        await activityLogService.logActivity(
          "delete_failed",
          "product",
          id,
          productName,
          {
            reason: "active_orders",
            order_count: activeOrderCount,
          },
        );

        throw new Error(
          `Cannot delete product "${productName}". It has ${activeOrderCount} active order${activeOrderCount !== 1 ? "s" : ""}.`,
        );
      }

      // Clean up product image from Supabase storage if it exists
      let productImageCleanupSuccess = false;
      if (product && product.image) {
        try {
          // Extract filename from the product image URL
          const imageFilename = this.extractFilenameFromImageUrl(product.image);

          if (imageFilename) {
            const { error: storageError } = await supabase.storage
              .from("product-images")
              .remove([imageFilename]);

            if (storageError) {
              console.warn(
                `Failed to delete product image ${imageFilename}:`,
                storageError.message,
              );
            } else {
              productImageCleanupSuccess = true;
            }
          }
        } catch (cleanupError) {
          console.warn("Error during product image cleanup:", cleanupError);
          // Don't throw - continue with product deletion
        }
      }

      // Final safety check before deletion - verify no digital downloads exist
      // This prevents foreign key constraint violations
      const { data: finalDownloadCheck, error: finalCheckError } =
        await supabase
          .from("digital_downloads")
          .select("id")
          .eq("product_id", id)
          .limit(1);

      if (finalCheckError) {
        console.error("Final download check failed:", finalCheckError);
        throw finalCheckError;
      }

      if (finalDownloadCheck && finalDownloadCheck.length > 0) {
        // This should never happen if the logic above is working correctly
        console.error(
          "CRITICAL: Attempted to delete product with digital downloads. Archiving instead.",
        );
        await this.updateProductStatus(id, "archived");
        throw new Error(
          `Cannot delete product "${productName}". It has digital download records. The product has been archived instead.`,
        );
      }

      // Proceed with actual deletion (only for products with no history)
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq("id", id)
        .eq("website_id", this.getWebsiteId());

      if (error) {
        console.error("Delete query failed:", error);
        throw error;
      }

      // Log successful deletion with cleanup status
      await activityLogService.logDelete("product", id, productName, {
        product_image_cleanup_success: productImageCleanupSuccess,
        image_url: product?.image || null,
      });

      return true;
    } catch (error) {
      console.error("Error in deleteProduct:", error);
      throw error;
    }
  }

  async updateProductStatus(
    id: string,
    status: "draft" | "published" | "archived",
  ): Promise<ProductWithDigital> {
    const oldProduct = await this.getProductById(id);
    const oldStatus = oldProduct.status;

    const { data, error } = await supabase
      .from(this.tableName)
      .update({
        status,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .eq("website_id", this.getWebsiteId())
      .select()
      .single();

    if (error) throw error;

    await activityLogService.logStatusChange(
      "product",
      id,
      data.name,
      oldStatus,
      status,
    );

    return data;
  }

  // New method for explicit archival
  async archiveProduct(id: string): Promise<ProductWithDigital> {
    // Initialize activity log service
    await activityLogService.initialize();

    const product = await this.getProductById(id);

    if (product.status === "archived") {
      throw new Error("Product is already archived");
    }

    // Get deletion check info for logging
    const deletionCheck = await this.canDeleteProduct(id);

    const archivedProduct = await this.updateProductStatus(id, "archived");

    // Log detailed archival information
    await activityLogService.logActivity(
      "archive",
      "product",
      id,
      product.name,
      {
        reason: "manual_archive",
        previous_status: product.status,
        order_count: deletionCheck.totalOrderCount,
        download_count: deletionCheck.downloadCount,
      },
    );

    return archivedProduct;
  }

  // Method to restore archived products
  async restoreProduct(
    id: string,
    newStatus: "draft" | "published" = "draft",
  ): Promise<ProductWithDigital> {
    const product = await this.getProductById(id);

    if (product.status !== "archived") {
      throw new Error("Only archived products can be restored");
    }

    const restoredProduct = await this.updateProductStatus(id, newStatus);

    await activityLogService.logActivity(
      "restore",
      "product",
      id,
      product.name,
      {
        previous_status: "archived",
        new_status: newStatus,
      },
    );

    return restoredProduct;
  }

  async updateInventory(
    id: string,
    stockQuantity: number,
  ): Promise<ProductWithDigital> {
    const { data, error } = await supabase
      .from(this.tableName)
      .update({
        stock_quantity: stockQuantity,
        in_stock: stockQuantity > 0,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .eq("website_id", this.getWebsiteId())
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async canDeleteProduct(productId: string): Promise<CanDeleteResult> {
    try {
      // Check for active orders containing this product
      const { data: activeOrders, error: activeError } = await supabase
        .from("order_items")
        .select("id, orders!inner(status)")
        .eq("product_id", productId)
        .in("orders.status", ["paid", "processing", "shipped"]);

      if (activeError) throw activeError;

      // Check for ANY order history containing this product
      const { data: allOrders, error: allError } = await supabase
        .from("order_items")
        .select("id")
        .eq("product_id", productId);

      if (allError) throw allError;

      // Check for digital downloads
      const { data: downloads, error: downloadError } = await supabase
        .from("digital_downloads")
        .select("id")
        .eq("product_id", productId);

      if (downloadError) throw downloadError;

      // Defensive null checks - ensure we always have arrays
      const activeOrderCount = (activeOrders || []).length;
      const totalOrderCount = (allOrders || []).length;
      const downloadCount = (downloads || []).length;

      // Product should be archived if it has any order history or downloads
      const shouldArchive = totalOrderCount > 0 || downloadCount > 0;

      // Product can only be truly deleted if:
      // 1. No active orders AND
      // 2. No order history AND
      // 3. No downloads
      const canDelete = activeOrderCount === 0 && !shouldArchive;

      return {
        canDelete,
        activeOrderCount,
        shouldArchive,
        totalOrderCount,
        downloadCount,
        recommendation: shouldArchive ? "archive" : "delete",
      };
    } catch (error) {
      console.error("Error checking product deletion:", error);
      throw error;
    }
  }

  // Digital product specific methods
  async createDigitalProduct(
    productData: DigitalProductFormData,
  ): Promise<ProductWithDigital> {
    const digitalProductData: ProductData = {
      ...productData,
      product_type: "digital",
      stock_quantity: 0, // Digital products don't track stock
      in_stock: true, // Digital products are always in stock
      max_quantity: null, // Digital products don't have quantity limits
    };

    return this.createProduct(digitalProductData);
  }

  async updateDigitalMetadata(
    id: string,
    metadata: DigitalMetadata,
  ): Promise<ProductWithDigital> {
    const { data, error } = await supabase
      .from(this.tableName)
      .update({
        digital_metadata: metadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .eq("website_id", this.getWebsiteId())
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .single();

    if (error) throw error;

    // Log the metadata update
    await activityLogService.logUpdate("product", id, data.name, {
      changes: {
        digital_metadata: { from: "updated", to: "updated" },
      },
    });

    return data;
  }

  async getDigitalProducts(): Promise<ProductWithDigital[]> {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(
        `
        *,
        product_categories (
          id,
          name
        )
      `,
      )
      .eq("website_id", this.getWebsiteId())
      .eq("product_type", "digital")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Extract filename from a Supabase storage URL for product images
   * @param url - The full URL to the image
   * @returns The filename or null if extraction fails
   */
  private extractFilenameFromImageUrl(url: string): string | null {
    if (!url) return null;

    try {
      // Handle Supabase public URLs
      // Format: https://project.supabase.co/storage/v1/object/public/product-images/filename
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split("/");

      // Find the filename part (last segment after "product-images")
      const productImagesIndex = pathParts.indexOf("product-images");
      if (
        productImagesIndex >= 0 &&
        productImagesIndex < pathParts.length - 1
      ) {
        return pathParts[productImagesIndex + 1];
      }

      // Fallback: just use the last part of the path if it looks like a filename
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart && lastPart.includes(".")) {
        return lastPart;
      }

      return null;
    } catch (error) {
      console.warn(`Could not extract filename from URL: ${url}`, error);
      return null;
    }
  }
}

export const adminProductService = new AdminProductService();
